import moment, {Duration, Moment} from 'moment-timezone';
import {unitOfTime} from 'moment';

moment.updateLocale(moment.locale(), {
  // change to monday-based weeks
  week: { dow: 1, doy: 4 }
});

export const DAY_NAMES = moment.weekdays();
// change to 0 = Monday
const monday = DAY_NAMES[0];
DAY_NAMES.splice(0, 1);
DAY_NAMES.push(monday);

export type TimeZone = 'UTC'|string;

export class DateTime {
  private readonly _value: moment.Moment;

  public get year(): number { return this._value.year(); }
  public get week(): number { return this._value.week(); }
  public get month(): number { return this._value.month(); }
  public get hour(): number { return this._value.hour(); }

  public static now(timezone?: TimeZone): DateTime {
    return timezone ? new DateTime(moment.tz(timezone)) : new DateTime(moment.tz());
  }

  public static today(timezone?: TimeZone): DateTime {
    const dt = DateTime.now(timezone);
    dt._value.startOf('day');
    return dt;
  }

  public get dayOfWeek(): number {
    return this._value.weekday();
  }

  public get date(): DateTime {
    const dt = this.clone();
    dt._value.startOf('day');
    return dt;
  }

  public get time(): TimeSpan {
    const m = this._value;
    const time = m.diff(m.clone().startOf('day'), 'seconds');
    return new TimeSpan(time);
  }

  public get innerValue(): Moment {
    return this._value;
  }

  constructor(value: Date|moment.Moment, timezone?: TimeZone) {
    this._value = timezone ? moment.tz(value, timezone) : moment(value);
  }

  public static parse(value: string, timezone?: TimeZone): DateTime {
    if(!value) {
      throw new Error("Value is required");
    }
    const m = moment.parseZone(value);
    if(timezone) {
      m.tz(timezone, true);
    }
    return new DateTime(m, m.tz() as TimeZone);
  }

  public static tryParse(value: string, timezone?: TimeZone): DateTime|null {
    try {
      return DateTime.parse(value, timezone);
    } catch {
      console.warn("Error parsing date: " + value);
      return null;
    }
  }

  public startOf(unit: unitOfTime.StartOf): DateTime {
    const dt = this.clone();
    dt._value.startOf(unit);
    return dt;
  }

  public endOf(unit: unitOfTime.StartOf): DateTime {
    const dt = this.clone();
    dt._value.endOf(unit);
    return dt;
  }

  public toDate(): Date {
    return this._value.toDate();
  }

  public clone(): DateTime {
    return new DateTime(this._value);
  }

  public add(amount: TimeSpan): DateTime {
    const dt = this.clone();
    dt._value.add(amount.innerValue);
    return dt;
  }

  public addUnits(amount: number, units: unitOfTime.DurationConstructor): DateTime {
    const dt = this.clone();
    dt._value.add(amount, units);
    return dt;
  }

  public subtract(amount: TimeSpan): DateTime {
    const dt = this.clone();
    dt._value.subtract(amount.innerValue);
    return dt;
  }

  public subtractUnits(amount: number, units: unitOfTime.DurationConstructor): DateTime {
    const dt = this.clone();
    dt._value.subtract(amount, units);
    return dt;
  }

  public diff(other: DateTime): TimeSpan {
    const duration = this._value.diff(other._value, 'seconds');
    return new TimeSpan(duration);
  }

  public unitDiff(other: DateTime, unit: unitOfTime.Diff): number {
    return this._value.diff(other._value, unit);
  }

  public withTimeZone(tz: TimeZone, keepLocalTime = false): DateTime {
    const m = this._value.clone();
    m.tz(tz, keepLocalTime);
    return new DateTime(m);
  }

  public format(format?: string): string {
    return this._value.format(format);
  }

  public equals(other: DateTime, unit?: unitOfTime.StartOf): boolean {
    return this._value.isSame(other._value, unit);
  }

  public lessThan(other: DateTime, unit?: unitOfTime.StartOf): boolean {
    return this._value.isBefore(other._value, unit);
  }

  public lessThanOrEqual(other: DateTime, unit?: unitOfTime.StartOf): boolean {
    return this._value.isSameOrBefore(other._value, unit);
  }

  public greaterThan(other: DateTime, unit?: unitOfTime.StartOf): boolean {
    return this._value.isAfter(other._value, unit);
  }

  public greaterThanOrEqual(other: DateTime, unit?: unitOfTime.StartOf): boolean {
    return this._value.isSameOrAfter(other._value, unit);
  }

  public toISOString(): string {
    return this._value.toISOString(true);
  }

  public toString(): string {
    return this._value.toISOString(true);
  }

  public toJSON(): string {
    return this._value.toISOString(true);
  }
}

export class TimeSpan {
  private readonly _value: Duration;

  public get totalDays(): number { return this._value.asDays(); }
  public get totalSeconds(): number { return this._value.asSeconds(); }
  public get totalHours(): number { return this._value.asHours(); }
  public get hours(): number { return this._value.hours(); }
  public get innerValue(): Duration { return this._value; }

  constructor(time?:{years?: number, months?: number, weeks?: number, days?: number, hours?: number, minutes?: number, seconds?: number}|number|Duration) {
    if(!time) {
      this._value = moment.duration(0);
    } else if (typeof (time) == 'number') {
      this._value = moment.duration(time, 'seconds');
    } else {
      this._value = moment.duration(time);
    }
  }

  public static parse(value: string): TimeSpan {
    if(!value) {
      throw new Error("Value is required");
    }
    const ts = new TimeSpan();
    ts._value.add(moment.duration(value));
    return ts;
  }

  public static tryParse(value: string): TimeSpan|null {
    try {
      return TimeSpan.parse(value);
    } catch {
      return null;
    }
  }

  public format(format?: string): string {
    if(!format) {
      format = 'H:mm:ss';
    }
    return moment(0).utc().add(this._value).format(format);
  }

  public clone(): TimeSpan {
    return new TimeSpan(this._value.clone());
  }

  public multiply(factor: number): TimeSpan {
    const ts = new TimeSpan();
    ts._value.add({milliseconds: this._value.asMilliseconds() * factor});
    return ts;
  }

  public add(other: TimeSpan): TimeSpan {
    const ts = new TimeSpan();
    ts._value.add(this._value);
    ts._value.add(other._value);
    return ts;
  }

  public subtract(other: TimeSpan): TimeSpan {
    const ts = new TimeSpan();
    ts._value.add(this._value);
    ts._value.subtract(other._value);
    return ts;
  }

  public equals(other: TimeSpan): boolean {
    return this.totalSeconds === other.totalSeconds;
  }

  public lessThan(other: TimeSpan): boolean {
    return this._value.asSeconds() < other._value.asSeconds();
  }

  public greaterThan(other: TimeSpan): boolean {
    return this._value.asSeconds() > other._value.asSeconds();
  }

  public isBetween(min: TimeSpan, max: TimeSpan, minInclusive: boolean = true, maxInclusive: boolean = true): boolean {
    const seconds = this.totalSeconds;
    const minSeconds = min.totalSeconds;
    const maxSeconds = max.totalSeconds;
    if(seconds < minSeconds || (!minInclusive && seconds === minSeconds)) {
      return false;
    } else if(seconds > maxSeconds || (!maxInclusive && seconds === maxSeconds)) {
      return false;
    } else {
      return true;
    }
  }

  public toString(): string {
    return this.format();
  }

  public toJSON(): string {
    return this.format();
  }
}

export interface DateRange {
  start: DateTime;
  end: DateTime;
}